<template>
  <div class="container mt-4">
    <h2 class="mb-4">{{ conferenceId ? 'Editar Jornada' : 'Crear Jornada' }}</h2>
    <form @submit.prevent="handleSubmit" class="card p-4 shadow-sm">
      <div v-if="error" class="alert alert-danger">{{ error }}</div>
      <div v-if="success" class="alert alert-success">{{ success }}</div>
      <div class="row mb-3">
        <div class="col-8">
          <label class="form-label">Nombre *</label>
          <input v-model="form.name" type="text" class="form-control" required maxlength="255" />
        </div>
        <div class="col-4">
          <label class="form-label">Estado *</label>
          <select v-model="form.status" class="form-control" required>
            <option :value="1">Activo</option>
            <option :value="2">Inactivo</option>
          </select>
        </div>
      </div>
      <div class="mb-3">
        <label class="form-label">Descripción (Uso interno)</label>
        <textarea v-model="form.description" class="form-control" maxlength="255"></textarea>
      </div>
      <div class="row mb-3">
        <div class="col">
          <label class="form-label">Audiencia Dirigida*</label>
          <textarea v-model="form.audience_target" class="form-control" maxlength="255"></textarea>
        </div>
      </div>
      <div class="row mb-3">
        <div class="col-6">
          <label class="form-label">Sala *</label>
          <select v-model.number="form.room_id" class="form-control" required>
            <option disabled value="">Seleccione una sala</option>
            <option v-for="room in rooms" :key="room.id" :value="room.id">
              {{ room.name }}
            </option>
          </select>
        </div>
        <div class="col-6">
          <label class="form-label">Cupos *</label>
          <input v-model="form.audience" type="number" class="form-control" required />
        </div>
      </div>

      <div class="row mb-3">
        <div class="col">
          <label class="form-label">Hora de inicio *</label>
          <input v-model="form.start_time" type="time" class="form-control" required />
        </div>
        <div class="col">
          <label class="form-label">Hora de fin *</label>
          <input v-model="form.end_time" type="time" class="form-control" required />
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-12 col-md-4">
          <label class="form-label">Rubros</label>
          <multiselect v-model="selectedVehicleTypes" :options="vehicleTypes" :multiple="true" :close-on-select="false"
            :clear-on-select="false" :preserve-search="true" placeholder="Seleccione rubros" label="name" track-by="id"
            :preselect-first="false" class="multiselect-custom" />
        </div>
        <div class="col-12 col-md-4">
          <label class="form-label">Áreas</label>
          <multiselect v-model="selectedAreas" :options="areas" :multiple="true" :close-on-select="false"
            :clear-on-select="false" :preserve-search="true" placeholder="Seleccione una o más áreas" label="name"
            track-by="id" :preselect-first="false" class="multiselect-custom" />
        </div>
        <div class="col-12 col-md-4">
          <label class="form-label">Cargos</label>
          <multiselect v-model="selectedPositions" :options="filteredPositions" :multiple="true"
            :close-on-select="false" :clear-on-select="false" :preserve-search="true"
            placeholder="Seleccione uno o más cargos" label="name" track-by="id" :preselect-first="false"
            class="multiselect-custom">
            <template #option="{ option }">
              <div>
                <strong>{{ option.area?.name || 'Sin área' }}:</strong>
                {{ option.name }}
              </div>
            </template>
          </multiselect>
        </div>
      </div>

      <div class="row mb-3">
        <div class="col-6">
          <div class="mb-3">
            <label class="form-label">Imagen</label>
            <div class="image-upload-container">
              <button type="button" @click="openImageManager" class="btn btn-outline-primary image-manager-btn">
                <i class="fas fa-images"></i> Seleccionar imagen
              </button>
              <div v-if="selectedImageUrl" class="selected-image-preview">
                <img :src="selectedImageUrl" alt="Imagen seleccionada" class="preview-image" @error="handleImageError"
                  @load="handleImageLoad" />

              </div>
              <div v-else class="debug-info">
                <p>No hay imagen seleccionada</p>
              </div>
            </div>
          </div>
        </div>
        <div class="col-6">
          <label class="form-label">Tipo *</label>
          <select v-model.number="form.type" class="form-control" required>
            <option disabled value="">Seleccione tipo</option>
            <option :value="1">Charla</option>
            <option :value="2">Workshop</option>
          </select>
        </div>
      </div>

      <div class="mb-3">
        <label class="form-label">Información (Visible en la web)</label>
        <textarea v-model="form.information" class="form-control"></textarea>
      </div>
      <div class="d-flex gap-2 justify-content-end">
        <button type="submit" class="btn btn-primary">{{ conferenceId ? 'Actualizar' : 'Guardar' }}</button>
        <button type="button" class="btn btn-secondary" @click="handleCancel">Cancelar</button>
      </div>
    </form>

    <!-- Modal del gestor de imágenes -->
    <ImageManagerModal :isOpen="showImageManager" @close="showImageManager = false"
      @image-selected="handleImageSelected" />
  </div>
</template>

<script setup>
import { ref, onMounted, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from '@/plugins/axios';
import Swal from 'sweetalert2';
import ImageManagerModal from '@/components/shared/ImageManagerModal.vue';
import Multiselect from 'vue-multiselect';
import { fetchVehicleTypes } from '@/services/api/circulares';
import { fetchAreas } from '@/services/api/area';
import { fetchPositions } from '@/services/api/position';

const route = useRoute();
const router = useRouter();
const id = route.params.id;
const conferenceId = route.params.conferenceId;

const form = ref({
  name: '',
  description: '',
  start_time: '',
  end_time: '',
  audience: null,
  type: '',
  information: '',
  status: 1,
  room_id: '',
  audience_target: '',
  vehicle_types: [],
  areas: [],
  positions: []
});

const selectedImageUrl = ref('');
const showImageManager = ref(false);
const error = ref('');
const success = ref('');
const rooms = ref([]);
const vehicleTypes = ref([]);
const selectedVehicleTypes = ref([]);
const areas = ref([]);
const selectedAreas = ref([]);
const positions = ref([]);
const filteredPositions = ref([]);
const selectedPositions = ref([]);

watch(
  selectedVehicleTypes,
  newVal => {
    form.value.vehicle_types = Array.isArray(newVal) ? newVal.map(type => type.id) : [];
  },
  { deep: true }
);

watch(
  selectedAreas,
  newVal => {
    const ids = Array.isArray(newVal)
      ? newVal
        .map(area => area?.id)
        .filter(id => id !== undefined && id !== null)
      : [];
    form.value.areas = ids;
    filterPositionsByAreas();
    if (selectedPositions.value.length > 0) {
      const allowedIds = new Set(
        filteredPositions.value
          .map(position => getComparableId(position?.id))
          .filter(id => id !== null)
      );
      selectedPositions.value = selectedPositions.value.filter(position =>
        allowedIds.has(getComparableId(position?.id))
      );
    }
  },
  { deep: true }
);

watch(
  selectedPositions,
  newVal => {
    const ids = Array.isArray(newVal)
      ? newVal
        .map(position => position?.id)
        .filter(id => id !== undefined && id !== null)
      : [];
    form.value.positions = ids;
  },
  { deep: true }
);

const getComparableId = value => {
  if (value === null || value === undefined) {
    return null;
  }
  const numeric = Number(value);
  return Number.isNaN(numeric) ? String(value) : String(numeric);
};

const dedupeIdsPreserveType = collection => {
  if (!Array.isArray(collection)) {
    return [];
  }
  const map = new Map();
  collection.forEach(item => {
    const key = getComparableId(item);
    if (key !== null && !map.has(key)) {
      map.set(key, item);
    }
  });
  return Array.from(map.values());
};

const getPositionAreaId = position => {
  if (!position || typeof position !== 'object') {
    return null;
  }

  const candidateKeys = [
    position.area_id,
    position.areaId,
    position.area?.id,
    position.area?.data?.id,
    position.pivot?.area_id,
    position.pivot?.areaId,
  ];

  for (const value of candidateKeys) {
    if (value !== undefined && value !== null) {
      return value;
    }
  }

  if (Array.isArray(position.area) && position.area.length > 0) {
    const first = position.area[0];
    if (first?.id !== undefined && first.id !== null) {
      return first.id;
    }
  }

  return null;
};

const filterPositionsByAreas = () => {
  if (!Array.isArray(positions.value) || positions.value.length === 0) {
    filteredPositions.value = [];
    return;
  }
  if (!Array.isArray(selectedAreas.value) || selectedAreas.value.length === 0) {
    filteredPositions.value = positions.value.slice();
    return;
  }
  const areaIds = new Set(
    selectedAreas.value
      .map(area => getComparableId(area?.id))
      .filter(id => id !== null)
  );
  filteredPositions.value = positions.value.filter(position => {
    const areaId = getPositionAreaId(position);
    if (areaId === null) {
      return false;
    }
    return areaIds.has(getComparableId(areaId));
  });
};

const requiresPositionSelection = () => {
  if (!Array.isArray(selectedAreas.value) || selectedAreas.value.length === 0) {
    return false;
  }
  const areaIds = new Set(
    selectedAreas.value
      .map(area => getComparableId(area?.id))
      .filter(id => id !== null)
  );
  if (areaIds.size === 0) {
    return false;
  }
  return positions.value.some(position => {
    const areaId = getPositionAreaId(position);
    if (areaId === null) {
      return false;
    }
    return areaIds.has(getComparableId(areaId));
  });
};

const loadAreas = async () => {
  try {
    const response = await fetchAreas();
    const data = response?.data ?? response ?? [];
    areas.value = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
  } catch (e) {
    console.error('Error al cargar áreas:', e);
    areas.value = [];
  }
};

const loadPositions = async () => {
  try {
    const response = await fetchPositions();
    positions.value = Array.isArray(response) ? response : [];
    filterPositionsByAreas();
  } catch (e) {
    console.error('Error al cargar cargos:', e);
    positions.value = [];
    filteredPositions.value = [];
  }
};

// Función para cargar las salas
const fetchRooms = async () => {
  try {
    const response = await axios.get('/conferences/rooms');
    console.log('Respuesta de salas:', response.data);

    // La API devuelve un array de objetos con { id, name }
    const roomsData = Array.isArray(response.data?.data)
      ? response.data.data
      : (Array.isArray(response.data) ? response.data : []);

    rooms.value = roomsData.map((r) => ({ id: r.id, name: r.name }));

    console.log('Salas cargadas:', rooms.value);
  } catch (error) {
    console.error('Error al cargar salas:', error);
    rooms.value = [];
  }
};

// Cargar datos si es edición
onMounted(async () => {
  // Cargar salas siempre
  await fetchRooms();
  try {
    const response = await fetchVehicleTypes();
    vehicleTypes.value = response?.data ?? response ?? [];
  } catch (e) {
    console.error('Error al cargar tipos de vehículos:', e);
    vehicleTypes.value = [];
  }
  await loadAreas();
  await loadPositions();

  if (conferenceId) {
    try {
      const response = await axios.get(`/conferences/${conferenceId}`);
      const data = response.data.data;
      console.log('Datos completos de la conferencia:', data);
      const roomIdFromApi = (data?.room?.id ?? data?.room);
      form.value = {
        name: data.name || '',
        description: data.description || '',
        start_time: data.start_time ? data.start_time.slice(0, 5) : '',
        end_time: data.end_time ? data.end_time.slice(0, 5) : '',
        audience: data.audience || null,
        type: data.type || '',
        information: data.information || '',
        audience_target: data.audience_target || '',
        status: data.status !== undefined ? data.status : 1,
        room_id: roomIdFromApi !== undefined && roomIdFromApi !== null
          ? Number(roomIdFromApi)
          : '',
        vehicle_types: Array.isArray(data?.vehicle_types)
          ? data.vehicle_types
            .map(item => (typeof item === 'object' ? item.id : item))
            .filter(id => id !== undefined && id !== null)
          : [],
        areas: Array.isArray(data?.areas)
          ? data.areas
            .map(item => {
              if (item === null || item === undefined) {
                return null;
              }
              if (typeof item === 'object') {
                if (Array.isArray(item?.data) && item.data.length > 0) {
                  const first = item.data[0];
                  return first?.id ?? null;
                }
                return item.id ?? item.area_id ?? item.areaId ?? null;
              }
              return item;
            })
            .filter(id => id !== undefined && id !== null)
          : [],
        positions: Array.isArray(data?.positions)
          ? data.positions
            .map(item => (typeof item === 'object' ? item.id : item))
            .filter(id => id !== undefined && id !== null)
          : []
      };
      const vehicleIds = form.value.vehicle_types;
      selectedVehicleTypes.value = vehicleTypes.value.filter(item => vehicleIds.includes(item.id));

      let areaIds = form.value.areas.slice();
      if (areaIds.length === 0 && Array.isArray(data?.positions)) {
        const derived = data.positions
          .map(position => getPositionAreaId(position))
          .filter(id => id !== undefined && id !== null);
        areaIds = dedupeIdsPreserveType(derived);
        form.value.areas = areaIds;
      } else {
        areaIds = dedupeIdsPreserveType(areaIds);
        form.value.areas = areaIds;
      }

      const areaIdSet = new Set(areaIds.map(id => getComparableId(id)));
      selectedAreas.value = areas.value.filter(area => areaIdSet.has(getComparableId(area.id)));

      await nextTick();
      filterPositionsByAreas();

      const positionIds = dedupeIdsPreserveType(form.value.positions);
      form.value.positions = positionIds;
      const positionIdSet = new Set(positionIds.map(id => getComparableId(id)));
      selectedPositions.value = positions.value.filter(position => positionIdSet.has(getComparableId(position.id)));

      // Si hay imagen, mostrarla
      if (data.image_url) {
        selectedImageUrl.value = data.image_url;
        console.log('Imagen cargada desde image_url:', data.image_url);
      } else if (data.image) {
        // Si solo tenemos el nombre del archivo, construir la URL completa usando la URL base del backend
        const backendBaseUrl = process.env.VUE_APP_ACARA_WEB_BACKEND_URL ?? 'http://127.0.0.1:8000/api/v1';
        // Remover /api/v1 de la URL base para obtener solo el dominio
        const backendUrl = backendBaseUrl.replace('/api/v1', '');
        selectedImageUrl.value = `${backendUrl}/${data.image}`;
        console.log('Imagen construida desde nombre:', selectedImageUrl.value);
        console.log('URL del backend:', backendUrl);
      }

      console.log('Status cargado:', data.status, 'Form status:', form.value.status);
    } catch (e) {
      error.value = 'No se pudo cargar la jornada para editar.';
    }
  }
});



const openImageManager = () => {
  showImageManager.value = true;
};

const handleImageSelected = (imageUrl) => {
  console.log('Imagen seleccionada:', imageUrl);
  selectedImageUrl.value = imageUrl;
  showImageManager.value = false;
};


const extractFileName = (url) => {
  if (!url) return '';
  try {
    const pathname = new URL(url).pathname;
    const prefix = '/images/conventions/';
    if (pathname.startsWith(prefix)) {
      return pathname.slice(prefix.length);
    }
    return pathname.split('/').pop();
  } catch {
    return url.split('/').pop();
  }
};

const handleImageError = (event) => {
  console.error('Error al cargar la imagen:', selectedImageUrl.value);
  console.error('Evento de error:', event);
  // Mostrar un mensaje de error o placeholder
  event.target.style.display = 'none';
  const container = event.target.parentElement;
  const errorDiv = document.createElement('div');
  errorDiv.className = 'image-error-placeholder';
  errorDiv.innerHTML = '<i class="fas fa-exclamation-triangle text-warning"></i><br><small>Error al cargar imagen</small>';
  container.appendChild(errorDiv);
};

const handleImageLoad = () => {
  console.log('Imagen cargada exitosamente:', selectedImageUrl.value);
};

const handleSubmit = async () => {
  error.value = '';
  success.value = '';

  // Validaciones
  if (!form.value.name || form.value.name.length > 255) {
    error.value = 'El nombre es obligatorio y no puede superar 255 caracteres.';
    return;
  }
  if (form.value.description && form.value.description.length > 255) {
    error.value = 'La descripción no puede superar 255 caracteres.';
    return;
  }
  if (!form.value.start_time || !form.value.end_time) {
    error.value = 'Debe ingresar la hora de inicio y fin.';
    return;
  }
  const start = form.value.start_time + ':00';
  const end = form.value.end_time + ':00';
  if (start >= end) {
    error.value = 'La hora de fin debe ser posterior a la de inicio.';
    return;
  }
  if (![1, 2].includes(form.value.type)) {
    error.value = 'El tipo es obligatorio y debe ser 1 o 2.';
    return;
  }
  if (!form.value.room_id) {
    error.value = 'Debe seleccionar una sala.';
    return;
  }
  if (requiresPositionSelection() && (!Array.isArray(selectedPositions.value) || selectedPositions.value.length === 0)) {
    error.value = 'Debe seleccionar al menos un cargo para las áreas elegidas.';
    return;
  }
  if (!selectedImageUrl.value && !conferenceId) {
    error.value = 'La imagen es obligatoria para crear una jornada.';
    return;
  }

  // Si estamos editando y no hay imagen seleccionada, mantener la imagen existente
  if (conferenceId && !selectedImageUrl.value) {
    console.log('Editando conferencia sin nueva imagen seleccionada');
  }

  // Enviar a la API
  try {
    const data = {
      name: form.value.name,
      description: form.value.description,
      start_time: start,
      end_time: end,
      audience: form.value.audience,
      type: form.value.type,
      information: form.value.information,
      convention_id: id,
      audience_target: form.value.audience_target,
      status: form.value.status,
      room: form.value.room_id,
      vehicle_types: form.value.vehicle_types,
      areas: form.value.areas,
      positions: form.value.positions
    };

    // Solo agregar imagen si hay una seleccionada
    if (selectedImageUrl.value) {
      data.image = extractFileName(selectedImageUrl.value);
    }

    if (conferenceId) {
      await axios.put(`/conferences/${conferenceId}?_method=PUT`, data);
      await Swal.fire({
        icon: 'success',
        title: '¡Jornada actualizada!',
        text: 'La jornada se actualizó correctamente.',
        confirmButtonColor: '#3085d6',
        showConfirmButton: true
      });
    } else {
      await axios.post('/conferences', data);
      await Swal.fire({
        icon: 'success',
        title: '¡Jornada creada!',
        text: 'La jornada se guardó correctamente.',
        confirmButtonColor: '#3085d6',
        showConfirmButton: true
      });
    }
    router.push({ name: 'Conference', params: { id }, query: { reload: Date.now() } });
  } catch (e) {
    error.value = e?.response?.data?.message || (conferenceId ? 'Error al actualizar la jornada.' : 'Error al crear la jornada.');
  }
};

const handleCancel = () => {
  router.push({ name: 'Conference', params: { id: route.params.id } });
};
</script>

<style scoped>
.card {
  border-radius: 1rem;
}

.image-upload-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}



.image-manager-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
}

.image-manager-btn i {
  font-size: 1.1rem;
}

.selected-image-preview {
  position: relative;
  display: inline-block;
  max-width: 200px;
  animation: fadeInScale 0.3s ease-in-out;
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.8);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

.preview-image {
  width: 100%;
  height: 120px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #e5e7eb;
  transition: all 0.2s ease;
  cursor: pointer;
}

.preview-image:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.remove-image-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.remove-image-btn:hover {
  transform: scale(1.1);
}

.image-error-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 120px;
  background-color: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  color: #6c757d;
  text-align: center;
}

.image-error-placeholder i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.debug-info {
  background-color: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: 4px;
  padding: 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #0369a1;
}
</style>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>